import cn from 'classnames';
import { Brand, brandDisplayName } from 'common/Brand';
import React from 'react';
import { Icon, IconColor, IconSize } from 'web-common/src/shared/design-system/components/Icon';

export enum TableRunLogoVariant {
  /** Endorsed with "Powered by Shef" — the default. */
  Standard = 'standard',
  Compact = 'compact',
}

export enum TableRunLogoBackground {
  /** Cream or white: red mark, plum wordmark. */
  Light = 'light',
  /** Plum, red or photography: cream mark and wordmark. */
  Dark = 'dark',
}

export enum TableRunLogoSize {
  /** 20px icon — the on-screen minimum, so compact only. */
  Small = 'small',
  /** 24px icon — the endorsed lockup's minimum. */
  Medium = 'medium',
  Large = 'large',
}

interface TableRunLogoBaseProps {
  background?: TableRunLogoBackground;
  className?: string;
}

type TableRunLogoProps = TableRunLogoBaseProps &
  (
    | { variant?: TableRunLogoVariant.Standard; size?: TableRunLogoSize.Medium | TableRunLogoSize.Large }
    | { variant: TableRunLogoVariant.Compact; size?: TableRunLogoSize }
  );

interface LockupMetrics {
  icon: IconSize;
  wordmark: string;
  iconGap: string;
  endorsement: string;
  divider: string;
  endorsementGap: string;
}

const METRICS: Record<TableRunLogoSize, LockupMetrics> = {
  [TableRunLogoSize.Small]: {
    icon: 20,
    wordmark: 'text-[19px]',
    iconGap: 'gap-1.5',
    endorsement: 'text-xs',
    divider: 'h-4',
    endorsementGap: 'gap-2',
  },
  [TableRunLogoSize.Medium]: {
    icon: 24,
    wordmark: 'text-[22px]',
    iconGap: 'gap-2',
    endorsement: 'text-[13px]',
    divider: 'h-[18px]',
    endorsementGap: 'gap-2.5',
  },
  [TableRunLogoSize.Large]: {
    icon: 32,
    wordmark: 'text-[30px]',
    iconGap: 'gap-2.5',
    endorsement: 'text-[13px]',
    divider: 'h-[22px]',
    endorsementGap: 'gap-[11px]',
  },
};

/** `Standard` hides "Powered by Shef" below `sm`, where the logo guide calls for the compact lockup. */
export const TableRunLogo: React.FC<TableRunLogoProps> = ({
  variant = TableRunLogoVariant.Standard,
  background = TableRunLogoBackground.Light,
  size = TableRunLogoSize.Medium,
  className,
}) => {
  const isDark = background === TableRunLogoBackground.Dark;
  const metrics = METRICS[size];

  return (
    <span className={cn('inline-flex items-center leading-none', metrics.endorsementGap, className)}>
      <span className={cn('inline-flex items-center', metrics.iconGap)}>
        <Icon
          name='tablerunMark'
          size={metrics.icon}
          color={isDark ? IconColor.TABLERUN_CREAM : IconColor.TABLERUN_RED}
          className='flex shrink-0'
          aria-hidden='true'
        />
        <span
          className={cn('font-sans font-medium tracking-[-0.02em]', metrics.wordmark, {
            'text-tablerun-plum': !isDark,
            'text-tablerun-cream': isDark,
          })}>
          {brandDisplayName(Brand.TableRun)}
        </span>
      </span>
      {variant === TableRunLogoVariant.Standard && (
        <span className={cn('flex items-center max-sm:hidden', metrics.endorsementGap)}>
          <span
            aria-hidden='true'
            className={cn('w-[1.5px]', metrics.divider, {
              'bg-tablerun-rule': !isDark,
              'bg-tablerun-cream/50': isDark,
            })}
          />
          <span
            className={cn('font-inter font-normal', metrics.endorsement, {
              'text-tablerun-stone': !isDark,
              'text-tablerun-cream': isDark,
            })}>
            Powered by {brandDisplayName(Brand.Shef)}
          </span>
        </span>
      )}
    </span>
  );
};
